<template>
    <currency-select
        type="crypto"
        show-search
        :value="selectedCryptoCurrency?.code"
        :allowed-values="allowedCryptoCurrencies"
        :disabled-values="disabledCryptoCurrencies"
        @created="selectCurrency"
        @updated="selectCurrency"
    >
        <template #opener="{ open, quickSelect, focus }">
            <text-field
                name="cryptoCurrency"
                class="cryptocurrency"
                readonly
                :value="selectedCryptoCurrency?.name ?? null"
                :focus="focus"
                @click="open"
                @keydown="quickSelect"
            >
                <template #label>{{ label }}</template>
                <template #prepend>
                    <icon v-if="selectedCryptoCurrency" :name="selectedCryptoCurrency.code" :alt="selectedCryptoCurrency.name" directory="currencies" />
                </template>
                <template #append>
                    <caret-icon direction="down" />
                </template>
            </text-field>
        </template>

        <template #header="{ close }">
            <view-header close-button @closed="close">{{ modalTitle }}</view-header>
        </template>
    </currency-select>
</template>

<script lang="ts" setup>
import CurrencySelect from '@/dollar-cost-average/components/currency-select.vue';
import { ViewHeader } from '@btcdirect/components';
import { CaretIcon, Icon } from '@btcdirect/components/icons';
import { isDisabled } from '@btcdirect/functions';
import { QuoteType } from '@btcdirect/shared/src/composables/api/quote/types';
import { Currency, CurrencyPair, useClientInfoStore, useCurrenciesStore, useWalletAddressesStore } from '@btcdirect/stores';
import { TextField, ValidatedStringFieldData } from 'out-of-the-vue';
import { computed, onMounted, reactive } from 'vue';

const emit = defineEmits<{ (event: 'created' | 'updated', data: ValidatedStringFieldData): void }>();

const props = defineProps<{ label: string; modalTitle: string; quoteType: QuoteType }>();

const currenciesStore = useCurrenciesStore();
const walletAddressesStore = useWalletAddressesStore();
const clientInfoStore = useClientInfoStore();

const state = reactive<Omit<ValidatedStringFieldData, 'value'>>({
    name: 'cryptoCurrency',
    valid: true,
    failed: []
});

const selectedCryptoCurrency = computed<Currency>(() => currenciesStore.selectedCryptoCurrency);
const allowedCryptoCurrencies = computed<string[]>(() =>
    !!clientInfoStore.client.walletVerificationRequired ? currenciesStore.cryptoCurrencies : walletAddressesStore.availableWalletAddressCurrencies
);

const disabledCryptoCurrencies = computed<string[]>(() => {
    return currenciesStore.disabledCurrencyPairs.reduce((result: string[], currency: CurrencyPair) => {
        if (isDisabled(currency, props.quoteType)) {
            result.push(currency.baseCurrency.code);
        }
        return result;
    }, []);
});

function selectCurrency(): void {
    emit('updated', { ...state, value: currenciesStore.selectedCryptoCurrency.code });
}

onMounted(() => {
    emit('created', { ...state, value: currenciesStore.selectedCryptoCurrency.code });
});
</script>

<style lang="scss" scoped>
.cryptocurrency {
    :deep(main.input:hover),
    :deep(input:hover) {
        cursor: pointer;
    }
}

:deep(.prepend) {
    .icon {
        width: 2em;
        margin-left: 0.875em;
        margin-right: 0;
    }
}
</style>
